import { toLowerCase } from 'utils/manipulateString';
import { ERROR_MESSAGES, ERROR_CODES } from 'config/apiErrorCodes';

const errObjTypes = {
    CHUNK_LOAD_ERROR: 'chunkloaderror',
    TYPE_ERROR: 'typeerror',
    ABORT_ERROR: 'aborterror'
};

export function isChunkLoadError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errCode = toLowerCase(errObj.code);
    const errMessage = toLowerCase(errObj.message);
    return [
        errName === errObjTypes.CHUNK_LOAD_ERROR,
        errCode === 'css_chunk_load_failed',
        errMessage.includes('loading chunk')
    ].some(e => e);
}

export function isUnauthorizedToGetAccessToken(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 401 ||
        (code === ERROR_CODES.AUTHORIZATION_FAILED.toLowerCase() &&
            message === ERROR_MESSAGES.TOKEN_REQUIRED.toLowerCase())
    );
}

export function isTooManyAttemptErr(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 429 && message === 'too many attempts.';
}

export function isOtpValidationFailedError(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 403 &&
        message === ERROR_MESSAGES.OTP_VALIDATION_FAILED.toLowerCase() &&
        code === ERROR_CODES.AUTHORIZATION_FAILED.toLowerCase()
    );
}

// 這個 admin 在指定的 sponsor 中根本沒有擔任管理員角色（查無）。
// 注意：RLA-AU0-F0 在 promotion-tool 的共用 Forbidden response 有另一種語意
// （message 為 "Unauthorized"），所以必須 message + code 雙重比對，不可放寬為只看 code。
export function isRoleAuthoritiesFailedError(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 403 &&
        message === ERROR_MESSAGES.ROLE_AUTHORITIES_FAILED.toLowerCase() &&
        code === ERROR_CODES.PERMISSION_DENIED.toLowerCase()
    );
}

// 有擔任管理員角色，但這個角色沒有權限做這件事。
// PMIVLD-AU0-F0 來自 promotion-tool 的活動權限檢查，ROLE-V0-F0 來自角色權限驗證，
// 兩者 message 都是 "Permission denied"。
export function isRoleNoPermissionError(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 403 &&
        message === ERROR_MESSAGES.PROMOTION_PERMISSION_DENIED.toLowerCase() &&
        [
            ERROR_CODES.PROMOTION_PERMISSION_DENIED.toLowerCase(),
            ERROR_CODES.ROLE_VALIDATION_FAILED.toLowerCase()
        ].includes(code)
    );
}

// 網址上的 sponsorId 根本不存在（亂打／已被刪除）。後端回的是 400 + SP-R1-FNF1 + message "404"，
// 不是 403 —— 所以三個 403 predicate 都比不中，過去只能落到「系統發生錯誤」或無 reason 的無權限頁。
// 語意上不是權限問題，呼叫端應導到帶 sponsor_not_exist reason 的無權限頁而非回報 Sentry。
export function isSponsorNotExistError(errObj = {}) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 400 &&
        code === ERROR_CODES.SPONSOR_NOT_EXIST.toLowerCase() &&
        message === ERROR_MESSAGES.NOT_FOUND.toLowerCase()
    );
}

export function isPermissionDeniedError(errObj = {}) {
    return isRoleAuthoritiesFailedError(errObj) || isRoleNoPermissionError(errObj);
}

export function isSponsorNotApprovedError(errObj = {}) {
    // 主辦單位尚未審核通過：屬正常業務狀態，不應視為錯誤回報 Sentry
    const { status, code } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'sp-v0-f0';
}

// Turnstile 驗證失敗：8 支帳號／權限端點（#86eymfrt2）一致回 HTTP 401 + code TSA-AU0-FU1
// （prompt-library backend/Api/promotion-tool/*.yaml，commit cf7060c）。
// 400 TSA-AU0-F0（token 缺漏）與 503 TSA-3RD-F0（驗證服務異常）語意不同，不屬於這條「reset 重試」路徑。
export function isTurnstileVerificationFailedResp(errObj = {}) {
    const { status, code } = normalizeApiErrInfo(errObj);
    return status === 401 && code === 'tsa-au0-fu1';
}

export function is404NotFoundError(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 404 || message === '404';
}

export function isFetchNetworkError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.ABORT_ERROR ||
        (errName === errObjTypes.TYPE_ERROR &&
            [
                errMessage === '已取消',
                errMessage === 'cancelled',
                errMessage === '要求逾時。',
                errMessage === 'the request timed out.',
                errMessage === 'network request failed',
                errMessage === 'the network connection was lost.',
                errMessage === '網路連線中斷。',
                errMessage === 'internet 連線已斷開。',
                errMessage === 'internet連線已斷開。',
                errMessage === '無法完成作業。軟體導致連線失敗',
                errMessage === 'networkerror when attempting to fetch resource.',
                errMessage === '無法連接伺服器。'
            ].some(e => e))
    );
}

function isOneOfErrors(checkers = [], errObj) {
    return checkers.some(c => c(errObj));
}

function isWebKitError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && [errMessage.startsWith('webkit')].some(e => e);
}

function isDNSLookupError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        [errMessage.includes('無法找到指定主機名稱的伺服器')].some(e => e)
    );
}

function isSSLConnectionError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        [errMessage.includes('發生ssl錯誤，無法建立與伺服器的安全連線')].some(e => e)
    );
}

function isFailedToLoadError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && errMessage === 'load failed';
}

function isFailedToFetchError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && errMessage === 'failed to fetch';
}

function isPreFlightError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR && errMessage === 'preflight response is not successful'
    );
}

function isCORSDueToServerNoResponse(errObj = {}) {
    // 當 server 無法回應時，包括無法回出正確 header 時，因為 header 沒有 allow origin 瀏覽器會當作 cors
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        errMessage.includes('is not allowed by access-control-allow-origin')
    );
}

function normalizeApiErrInfo(errObj = {}) {
    const { method, httpStatus, payload: { code, status_code: statusCode, data, message } = {} } =
        errObj.extraInfo ?? errObj;
    return {
        method,
        status: httpStatus,
        data: data ?? {},
        // 後端各服務的錯誤 envelope 欄位名不一致：fever-api 用 `code`，
        // promotion-tool 部分端點用 `status_code`。兩者都吃，否則 predicate 永遠比不中。
        code: String(code ?? statusCode).toLowerCase(),
        message: String(message).toLowerCase()
    };
}

const feverErrCodes = {
    FAILED_TO_FETCH: 'fv-g-ff-001',
    FAILED_TO_LOAD: 'fv-g-fl-001',
    ERR_PREFLIGHT: 'fv-g-pf-001',
    FAILED_RESP_CORS: 'fv-g-sfc-001',
    ERR_DNS_LOOK_UP: 'fv-g-dl-001',
    ERR_SSL_CONNECT: 'fv-g-ssl-001',
    ERR_WEBKIT: 'fv-g-wk-001',
    ERR_DUPLICATE_GET_WINNER: 'fv-p-gw-001',
    ERR_FORM_DUPLICATE: 'fv-f-001',
    ERR_CREATE_ENTRIES_TIMEOUT: 'fv-ce-001'
};

export function getGeneralErrCode(errObj) {
    switch (true) {
        case isFailedToFetchError(errObj):
            return feverErrCodes.FAILED_TO_FETCH;
        case isFailedToLoadError(errObj):
            return feverErrCodes.FAILED_TO_LOAD;
        case isPreFlightError(errObj):
            return feverErrCodes.ERR_PREFLIGHT;
        case isCORSDueToServerNoResponse(errObj):
            return feverErrCodes.FAILED_RESP_CORS;
        case isDNSLookupError(errObj):
            return feverErrCodes.ERR_DNS_LOOK_UP;
        case isSSLConnectionError(errObj):
            return feverErrCodes.ERR_SSL_CONNECT;
        case isWebKitError(errObj):
            return feverErrCodes.ERR_WEBKIT;
        default:
            return '';
    }
}

export function isNetworkResourceError(errObj = {}) {
    return isOneOfErrors([isChunkLoadError, isFetchNetworkError], errObj);
}
